import dayjs, {Dayjs} from "dayjs";
import { IGetGasTrendAnalysisRes } from '@/types/api/ultra-sonic-data/getGasTrendAnalysis';
import {IGetNotificationRes} from "@/types/api/notification/getNotification";
import {IGetCareRecordRes} from "@/types/api/care-record/getCareRecord";
import { color } from '@/styles';
import { generateTimeLabelArray } from '@/utils/arrayUtil';
import { Activity, findHighRiskTimeRange } from '@/utils/findHighRisk';
import { getRecordNames, recordColors } from "./common";
import { t } from 'i18next';
import { getDayStartToEndRange } from '@/utils/dateutil';

const intervalMin = 30;

interface HeatmapGraph {
  data: number[][];
  xCategories: string[];
  yCategories: string[];
}

interface ScatterItem {
  x: number;
  y: number;
  color: string;
  name: string;
}

interface HeatmapEntryBase {
  timestamp: string;
}

interface HeatmapPoint {
  day: string;
  time: string;
  level: number;
}

interface ExtractHeatmapOptions<T extends HeatmapEntryBase> {
  getLevel: (entry: T) => number;
  intervalMin: number;
}

interface TrendValue {
  timestamp: string,
  value: number,
}

interface ActivityTrendValue {
  timestamp: string,
  isActive: boolean,
  isChangedPosture: boolean,
}

interface DividedGasTrends {
  sensorTrend: TrendValue[],
  temperatureTrend: TrendValue[],
  humidityTrend: TrendValue[],
  activityTrend: ActivityTrendValue[],
}

interface HeatmapData {
  data: number[][];
  xCategories: string[];
  yCategories: string[];
}

interface ActivityPositionChangedPlot {
  x: number;
  y: number;
  color?: string;
}

interface CareRecordPlot {
  x: number;
  y: number;
  color: string;
  name: string;
}

interface NotificationPlot {
  x: number;
  y: number;
  color: string;
  name: string;
}

interface SensorData {
  updatedSensorSummaryHeatmap: HeatmapData;
  sensorHeatmapData: HeatmapData;
}

interface TemperatureData {
  updatedTemperatureSummaryHeatmap: HeatmapData;
  temperatureHeatmapData: HeatmapData;
}

interface HumidityData {
  updatedHumiditySummaryHeatmap: HeatmapData;
  humidityHeatmapData: HeatmapData;
}

// アクティビティ関連データの型
interface ActivityData {
  activityHeatmapData: HeatmapData;
  activityPositionChangedPlot: ActivityPositionChangedPlot[];
}

// 関数の返り値全体の型
export interface ProcessedHeatmapData {
  sensor: SensorData;
  temperature: TemperatureData;
  humidity: HumidityData;
  activity: ActivityData;
  highRiskTimes: Dayjs[][];
  careRecordPlot: CareRecordPlot[];
  notificationPlot: NotificationPlot[];
}

export function createHeatmapDatas(
    gasTrend: IGetGasTrendAnalysisRes,
    careRecords: IGetCareRecordRes[],
    notifications: IGetNotificationRes[],
    timeRange: string,
    selectedStartTime: string,
    selectedEndTime: string,): ProcessedHeatmapData {

  const divideEachTrends = (gasTrend: IGetGasTrendAnalysisRes): DividedGasTrends => {

    const sensorTrend: TrendValue[] = gasTrend.map((r) => ({timestamp: r.timestamp, value: Math.round(r.gasPercentage * 10) / 10}));
    const temperatureTrend: TrendValue[] = gasTrend.map((r) => ({timestamp: r.timestamp, value: Math.round(r.temperature * 10) / 10}));
    const humidityTrend: TrendValue[] = gasTrend.map((r) => ({timestamp: r.timestamp, value: Math.round(r.humidity * 10) / 10}));
    let activityTrend: ActivityTrendValue[] = gasTrend.map((r) => ({
      timestamp: r.timestamp, 
      isActive: r.isActive,
      isChangedPosture: r.isChangedPosture
    }));

    return {
      sensorTrend,
      temperatureTrend,
      humidityTrend,
      activityTrend
    };
  }

  const convertTimestamp = (timestamp: string | number, intervalMin: number): undefined | {minute: number, formattedTime: string, day: string} => {
    let currentTime = dayjs(timestamp);
    let [startOfDay, endOfDay] = getTimeRange(currentTime);

    if (!currentTime.isBetween(startOfDay, endOfDay, null, '[]')) {
      [startOfDay, endOfDay] = getTimeRange(currentTime.subtract(1, 'day'));
      if (!currentTime.isBetween(startOfDay, endOfDay, null, '[]')) return;
    }

    const minute = Math.floor(currentTime.minute() / intervalMin) * intervalMin;
    const formattedTime = `${currentTime.hour()}:${String(minute).padStart(2, '0')}`;
    const day = startOfDay.local().format('YYYY/MM/DD');

    return {minute, formattedTime, day}
  }

  const extractHeatmap = <T extends HeatmapEntryBase>(
      data: T[],
      options: ExtractHeatmapOptions<T>
  ) => {
    const { getLevel, intervalMin } = options;
    const dateSet = new Set<string>();
    const heatmapData: HeatmapPoint[] = [];
    const xCategories = setXAxisCategories(intervalMin);

    data.forEach((entry) => {
      const timestamps = convertTimestamp(entry.timestamp, intervalMin);
      if (!timestamps) return;
      const { formattedTime, day} = timestamps;
      dateSet.add(day);

      heatmapData.push({
        day,
        time: formattedTime,
        level: getLevel(entry),
      });
    });

    const yCategories = Array.from(dateSet).sort();
    const formattedData = heatmapData.map((r) => [
      xCategories.indexOf(r.time),
      yCategories.indexOf(r.day),
      r.level,
    ]);

    return {
      data: formattedData,
      xCategories,
      yCategories,
    };
  };

  // 日別Heatmap生成
  const createHeatmapData = (data: TrendValue[]) => {
    return extractHeatmap(data, {
      getLevel: (entry) => entry.value,
      intervalMin,
    });
  };
  const createActivityHeatmapData = (data: ActivityTrendValue[]) => {
    return extractHeatmap(data, {
      getLevel: (entry) => entry.isActive ? 1 : 0,
      intervalMin,
    });
  };

  const createHighRiskTimes = (activityTrend: ActivityTrendValue[], ...trendValuesList: TrendValue[][]): Dayjs[][] => {

    const divideTrendValues = (trendValuesList: TrendValue[][]): {valuesList: number[][], timestamps: Dayjs[]} => {
      const timestamps: Dayjs[] = trendValuesList[0].map((r) => dayjs(r.timestamp));

      const valuesList: number[][] = [];
      for (const _trendValues of trendValuesList) {
        const _values: number[] = _trendValues.map((r) => r.value);
        valuesList.push(_values);
      }

      return {valuesList, timestamps}
    }

    const {valuesList, timestamps} = divideTrendValues(trendValuesList)
    const activities: Activity[] = activityTrend.map((r) => ({timestamp: dayjs(r.timestamp), isActive: r.isActive, isPositionChanged: r.isChangedPosture}) )

    return findHighRiskTimeRange(valuesList, timestamps, activities);
  }

  const createHeatmapPlot = (data: ScatterItem[], xCategories: string[], yCategories: string[]) => {
    const plotData: { day: string; time: string; color: string; name: string }[] = [];

    data.forEach((r) => {
      const timestamps = convertTimestamp(r.x, intervalMin);
      if (!timestamps) return;
      const { formattedTime, day} = timestamps;

      plotData.push({
        day,
        time: formattedTime,
        color: r.color,
        name: r.name,
      });
    });

    return plotData.map((r) => {
      const x = xCategories.indexOf(r.time);
      const y = yCategories.indexOf(r.day);
      return { x, y, color: r.color, name: r.name };
    });

  };

  const createPositionChangedPlot = (trend: ActivityTrendValue[], xCategories: string[], yCategories: string[]) => {
    const filtered = trend.filter((r) => r.isChangedPosture);

    return filtered.map((entry) => {
      const timestamps = convertTimestamp(entry.timestamp, intervalMin);
      if (!timestamps) return null; // nullを返して後でフィルタリング
      const { formattedTime, day } = timestamps;
      const x = xCategories.indexOf(formattedTime);
      const y = yCategories.indexOf(day);
      return { x, y };
    }).filter((item): item is { x: number, y: number } => item !== null);
  };

  // 通知プロット生成
  const createNotificationScatterData = (data: IGetNotificationRes[]) => {
    const scatterData: ScatterItem[] = [];

    data.forEach((r) => {
      const x = dayjs(r.timestamp).valueOf();
      const y = 0; //通知プロットはグラフの一番下に描画する
      const colors =
          r.notificationType === 'gasPooped'
              ? color.notification_orange_odor
              : r.notificationType === 'gasPrediction'
                  ? color.notification_green
                  : null;
      const name =
          r.notificationType === 'gasPooped'
              ? t('odorDetection', '臭い検知')
              : r.notificationType === 'gasPrediction'
                  ? t('estimatedTimeOfDefecation', '排便予測時間')
                  : null;

      if (colors && name) {
        scatterData.push({ x, y, color: colors, name });
      }
    });
    return scatterData;
  };

  // ケア履歴プロット生成
  const createCareRecordScatter = (data: IGetCareRecordRes[]) => {
    const scatterData: ScatterItem[] = [];
    const recordNames = getRecordNames(t);

    const filteredData = data.filter((r) => {
      const urineHasValue: boolean = r.padUrineVolumeType !== null && r.padUrineVolumeType !== 'none';
      const defecationHasValue: boolean = r.defecationVolumeType !== null && r.defecationVolumeType !== 'none';
      const noneChecked: boolean = r.padUrineVolumeType === 'none' && r.defecationVolumeType === 'none';

      return urineHasValue || defecationHasValue || noneChecked;
    });

    filteredData.forEach((r) => {
      const x = dayjs(r.timestamp).valueOf();
      const y = 10; //通知プロットはグラフの一番下に描画する

      const padUrine: boolean = r.padUrineVolumeType !== 'none' && !!r.padUrineVolumeType;
      const defecation: boolean = r.defecationVolumeType !== 'none' && !!r.defecationVolumeType;

      const recordType = defecation && padUrine ? 'defecationAndUrine' : defecation ? 'defecation' : padUrine ? 'urine' : 'none';
      const color = recordColors[recordType];
      const name = recordNames[recordType];

      if (color && name) {
        scatterData.push({ x, y, color, name });
      }
    });
    return scatterData;
  };

  // 指定された日時の指定の開始時間から終了時間の範囲を返す
  const getTimeRange = (time: Dayjs) => {
    switch (timeRange) {
      case 'all-time': {
        // 今日の0時から翌日0時までの範囲を返す
        return getDayStartToEndRange(time, 0, 0);
      }
      case 'day-time': {
        // 今日の7時から19時までの範囲を返す
        const startHour = 7;
        const endHour = 19;
        return getDayStartToEndRange(time, startHour, endHour);
      }

      case 'night-time': {
        // 今日の19時から明日の7時までの範囲を返す
        const startHour = 19;
        const endHour = 7;
        return getDayStartToEndRange(time, startHour, endHour);
      }

      case 'selected-time': {
        // 自由設定時間を返す
        const startHour = +selectedStartTime.split(':')[0];
        const endHour = +selectedEndTime.split(':')[0];
        return getDayStartToEndRange(time, startHour, endHour);
      }
      default:
        throw new Error('Invalid selected timeRange');
    }
  };

  const createSummaryHeatmap = (heatmapData: HeatmapGraph) => {
    if (!heatmapData?.data?.length) {
      return {
        data: [[], [], []],
        xCategories: [],
        yCategories: [],
      };
    }

    const counts = new Map<number, number>();
    const sums = new Map<number, number>();
    const averageHeatmapData: number[][] = [];

    heatmapData.data.forEach(([x, , value]) => {
      counts.set(x, (counts.get(x) || 0) + 1);
      sums.set(x, (sums.get(x) || 0) + value);
    });

    for (let x = 0; x < heatmapData.xCategories.length; x++) {
      const count = counts.get(x) || 0;
      const sum = sums.get(x) || 0;
      const average = count !== 0 ? Math.round((sum / count) * 10) / 10 : 0;
      averageHeatmapData.push([x, 0, average]);
    }
    return {
      data: averageHeatmapData,
      xCategories: heatmapData.xCategories,
      yCategories: heatmapData.yCategories,
    };
  };

  // heatmap x軸のCategoriesを生成
  const setXAxisCategories = (intervalMin: number) => {
    let start = '7:00';
    let arrayLength = 12 * (60 / intervalMin);

    switch (timeRange) {
      case 'all-time': {
        start = '0:00';
        arrayLength = 24 * (60 / intervalMin);
        break;
      }
      case 'day-time': {
        start = '7:00';
        arrayLength = 12 * (60 / intervalMin);
        break;
      }
      case 'night-time': {
        start = '19:00';
        arrayLength = 12 * (60 / intervalMin);
        break;
      }
      case 'selected-time': {
        start = selectedStartTime;
        const startHour = +selectedStartTime.split(':')[0];
        const endHour = +selectedEndTime.split(':')[0];
        // startとendが同じ場合は24時間扱い
        arrayLength =
          startHour < endHour
            ? (endHour - startHour) * (60 / intervalMin)
            : (24 - (startHour - endHour)) * (60 / intervalMin);
        break;
      }
      default:
        throw new Error('Invalid selected timeRange');
    }
    return generateTimeLabelArray(start, intervalMin, arrayLength);
  };
  const { sensorTrend, temperatureTrend, humidityTrend, activityTrend } = divideEachTrends(gasTrend);

  const highRiskTimes: Dayjs[][] = createHighRiskTimes(activityTrend, sensorTrend, temperatureTrend, humidityTrend);

  // 日別尿量グラフ用ケア履歴データ生成
  const careRecordScatter = createCareRecordScatter(careRecords);

  // 日別尿量グラフ用通知データ生成
  const notificationScatter = createNotificationScatterData(notifications);

  // 排尿傾向分析heatmap生成
  const sensorHeatmapData = createHeatmapData(sensorTrend);
  const temperatureHeatmapData = createHeatmapData(temperatureTrend);
  const humidityHeatmapData = createHeatmapData(humidityTrend);
  const activityHeatmapData = createActivityHeatmapData(activityTrend);

  // 統計量heatmap生成
  const updatedSensorSummaryHeatmap = createSummaryHeatmap(sensorHeatmapData);
  const updatedTemperatureSummaryHeatmap = createSummaryHeatmap(temperatureHeatmapData);
  const updatedHumiditySummaryHeatmap = createSummaryHeatmap(humidityHeatmapData);

  const activityPositionChangedPlot = createPositionChangedPlot(activityTrend, activityHeatmapData.xCategories, activityHeatmapData.yCategories);

  // heatmap用ケア履歴データ生成
  const careRecordPlot = createHeatmapPlot(careRecordScatter, sensorHeatmapData.xCategories, sensorHeatmapData.yCategories);

  // heatmap用通知データ生成
  const notificationPlot = createHeatmapPlot(notificationScatter, sensorHeatmapData.xCategories, sensorHeatmapData.yCategories);

  return {
    'sensor': {updatedSensorSummaryHeatmap, sensorHeatmapData},
    'temperature': {updatedTemperatureSummaryHeatmap, temperatureHeatmapData},
    'humidity': {updatedHumiditySummaryHeatmap, humidityHeatmapData},
    'activity': {activityHeatmapData, activityPositionChangedPlot},
    highRiskTimes,
    careRecordPlot,
    notificationPlot,
  }
}
